import { useEffect, useState } from "react";
import { Navigate, useParams } from "react-router-dom";
import { Btn } from "@/components/ui/Btn/Btn";
import { AppShell } from "@/components/app/AppShell";
import { CreateBrandModal } from "@/components/app/CreateBrandModal";
import { useAuth } from "@/lib/AuthContext";
import { useBrands, useWorkspace } from "@/lib/queries";
import { setLastWorkspaceId } from "@/lib/workspacePref";
import styles from "./WorkspaceLanding.module.css";

/**
 * Landing for /w/:ws. The workspace no longer has a "Home" dashboard —
 * brand navigation lives in the app chrome now. So this route is purely
 * a router:
 *   - ≥1 brand → redirect into the first brand (last-brand pref doesn't
 *     exist yet, so brands[0]).
 *   - 0 brands → render the salvaged "create your first brand"
 *     onboarding (the only thing worth keeping from the old dashboard).
 */
export function WorkspaceLanding() {
  const { ws } = useParams<{ ws: string }>();
  const auth = useAuth();
  const wsQuery = useWorkspace(ws);
  const brandsQuery = useBrands(ws);
  const [createOpen, setCreateOpen] = useState(false);

  // Remember this as the last-viewed workspace (side effect → effect,
  // not render body).
  useEffect(() => {
    if (ws) setLastWorkspaceId(ws);
  }, [ws]);

  if (auth.status === "anonymous") {
    return <Navigate to={`/signin?next=/w/${ws}`} replace />;
  }

  const role = wsQuery.data?.role;
  const canManageBrands = role === "owner" || role === "admin";
  const brands = brandsQuery.data?.brands ?? [];

  // Once brands have loaded, send the user straight into a brand. No
  // last-brand pref exists, so the first brand is the target.
  const first = brands[0];
  if (brandsQuery.data && first) {
    return <Navigate to={`/w/${ws}/b/${first.id}`} replace />;
  }

  return (
    <AppShell currentWorkspaceId={ws}>
      <div className={styles.page}>
        {wsQuery.error && (
          <p className={styles.error}>Couldn’t load this workspace: {wsQuery.error.message}</p>
        )}
        {brandsQuery.error && (
          <p className={styles.error}>Couldn’t load brands: {brandsQuery.error.message}</p>
        )}

        {brandsQuery.isLoading && <p className={styles.muted}>Loading brands…</p>}

        {brandsQuery.data && brands.length === 0 && (
          <div className={styles.empty}>
            <span className={styles.emptyMark} aria-hidden="true">✦</span>
            <h2 className={styles.emptyTitle}>No brands yet.</h2>
            <p className={styles.emptyBody}>
              A brand is the “brain” the AI learns — its voice, audience, products, and
              platform rules. Create one to start generating drafts that sound like you.
            </p>
            {canManageBrands ? (
              <Btn variant="primary" onClick={() => setCreateOpen(true)}>
                Create your first brand
              </Btn>
            ) : (
              <p className={styles.muted}>Ask a workspace owner or admin to add a brand.</p>
            )}
          </div>
        )}
      </div>

      {ws && (
        <CreateBrandModal
          wsId={ws}
          open={createOpen}
          onClose={() => setCreateOpen(false)}
        />
      )}
    </AppShell>
  );
}
